<template>
	<div class="email-step" :class="`email-step--${variant}`">
		<h3 class="step-title">{{ copy.stepTitle }}</h3>
		<p class="step-desc">{{ copy.stepDesc }}</p>

		<div class="email-field" :class="{ invalid: showError }">
			<input
				v-model.trim="email"
				type="email"
				class="email-input"
				:placeholder="copy.placeholder"
				@input="handleInput"
				@blur="handleBlur"
				@focus="handleEmailFocus"
			/>
			<button v-if="email" class="clear-btn" type="button" @click="clearEmail">
				<img :src="clearIcon" :alt="copy.clearAlt" />
			</button>
		</div>

		<div v-if="showError" class="error-text">
			<img :src="warningIcon" :alt="copy.warningAlt" />
			<span>{{ copy.errorInvalid }}</span>
		</div>

		<template v-if="isIntegrated">
			<div class="code-row">
				<input
					v-model="verifyCode"
					type="text"
					class="code-input"
					inputmode="numeric"
					maxlength="6"
					:placeholder="copy.codePlaceholder"
					@input="handleCodeInput"
					@focus="handleCodeFocus"
				/>
				<button
					class="send-code-btn"
					type="button"
					:disabled="countdown > 0"
					@click="handleSendCode"
				>
					<span v-if="countdown > 0">{{ countdown }}s</span>
					<span v-else>{{ copy.sendCode }}</span>
				</button>
			</div>

			<button v-if="showSwitch" class="switch-link" type="button" @click="$emit('switch', 'phone')">
				{{ copy.switchPhone }}
			</button>

			<a :href="kefuLink" class="kefu" @click="$emit('kefu-click')">{{ copy.kefuHelp }}</a>

			<div class="next-btn-container">
				<button
					class="next-btn"
					:class="{ enabled: canSubmit }"
					type="button"
					@click="handleSubmit"
				>
					{{ copy.next }}
				</button>
			</div>
		</template>

		<template v-else>
			<button v-if="showSwitch" class="switch-link" type="button" @click="$emit('switch', 'phone')">
				{{ copy.switchPhone }}
			</button>

			<button class="next-btn" :class="{ enabled: canProceed }" type="button" @click="handleNext">
				{{ copy.next }}
			</button>
		</template>
	</div>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";
import EmailValidator from "email-validator";
import showMessage from "~/utils/showMessage";
import { useI18n } from "~/composables/useI18n";
import { LOGIN_AUTH_METHODS } from "~/composables/useSiteComponentTracking";
import clearIcon from "./images/clear.png";
import warningIcon from "./images/warning.png";

const props = defineProps({
	/** integrated: 一步式（新登录）| step: 多步式（企业中心） */
	variant: {
		type: String,
		default: "integrated",
		validator: v => ["integrated", "step"].includes(v),
	},
	showSwitch: { type: Boolean, default: true },
	presetEmail: { type: String, default: "" },
	countdown: { type: Number, default: 0 },
	kefuLink: { type: String, default: "" },
	/** step 变体 UI 語言：`zh-TW`（預設）| `en` */
	locale: { type: String, default: "zh-TW" },
});

const emit = defineEmits(["send-code", "submit", "next", "switch", "kefu-click"]);
const { locale: i18nLocale } = useI18n();
const logLoginEvent = inject("logLoginEvent", null);

const isIntegrated = computed(() => props.variant === "integrated");
const isEn = computed(() =>
	isIntegrated.value ? i18nLocale.value === "en" : props.locale === "en"
);

const integratedCopy = {
	en: {
		stepTitle: "Enter your email",
		stepDesc: "We protect your privacy. Others will not see this information.",
		placeholder: "Enter your email",
		codePlaceholder: "Enter verification code",
		sendCode: "Send code",
		kefuHelp: "Didn't receive the code?",
		clearAlt: "clear",
		warningAlt: "warning",
		errorInvalid: "Please enter a valid email address",
		switchPhone: "Use mobile number to sign up / sign in",
		next: "Login / Register",
		codeRequired: "Please enter the verification code",
		codeInvalid: "Please enter a 6-digit verification code",
	},
	zh: {
		stepTitle: "輸入您的 E-mail",
		stepDesc: "我們會保障您的隱私，其他人不會看到這項資料。",
		placeholder: "輸入您的E-mail",
		codePlaceholder: "請輸入驗證碼",
		sendCode: "發送驗證碼",
		kefuHelp: "無法收到驗證碼？",
		clearAlt: "clear",
		warningAlt: "warning",
		errorInvalid: "請輸入有效的電子郵箱",
		switchPhone: "使用手機號註冊/登錄",
		next: "登入 / 註冊",
		codeRequired: "請輸入驗證碼",
		codeInvalid: "請輸入6位數字驗證碼",
	},
};

const stepCopy = {
	en: {
		stepTitle: "Enter your email",
		stepDesc: "We protect your privacy. Others will not see this information.",
		placeholder: "Enter your email",
		clearAlt: "clear",
		warningAlt: "warning",
		errorInvalid: "Please enter a valid email address",
		switchPhone: "Use mobile number to sign up / sign in",
		next: "Next",
	},
	zh: {
		stepTitle: "輸入您的 E-mail",
		stepDesc: "我們會保障您的隱私，其他人不會看到這項資料。",
		placeholder: "輸入您的E-mail",
		clearAlt: "clear",
		warningAlt: "warning",
		errorInvalid: "請輸入有效的電子郵箱",
		switchPhone: "使用手機號註冊/登錄",
		next: "下一步",
	},
};

const copy = computed(() => {
	const source = isIntegrated.value ? integratedCopy : stepCopy;
	return isEn.value ? source.en : source.zh;
});

const email = ref("");
const verifyCode = ref("");
const touched = ref(false);

const isValid = computed(() => EmailValidator.validate(email.value));
const canSubmit = computed(() => isValid.value && verifyCode.value.length === 6);
const canProceed = computed(() => email.value.length > 0);
const showError = computed(() => touched.value && email.value.length > 0 && !isValid.value);

const handleInput = () => {
	if (touched.value) touched.value = email.value.length > 0;
};

const handleBlur = () => {
	touched.value = true;
};

const handleEmailFocus = () => {
	logLoginEvent?.("login_email_input_focus");
};

const handleCodeFocus = () => {
	logLoginEvent?.("verification_code_input_focus", {
		auth_method: LOGIN_AUTH_METHODS.EMAIL,
	});
};

const getFilledFieldCount = () => {
	let count = 0;
	if (email.value.trim()) count += 1;
	if (verifyCode.value.trim()) count += 1;
	return count;
};

const handleCodeInput = () => {
	verifyCode.value = verifyCode.value.replace(/\D/g, "");
};

const clearEmail = () => {
	email.value = "";
	touched.value = false;
};

const handleSendCode = () => {
	if (props.countdown > 0) return;
	touched.value = true;
	if (!isValid.value) return;
	emit("send-code", email.value);
};

const handleSubmit = async () => {
	touched.value = true;
	if (!isValid.value) return;
	if (!verifyCode.value) {
		await showMessage(copy.value.codeRequired, "warning");
		return;
	}
	if (!/^\d{6}$/.test(verifyCode.value)) {
		await showMessage(copy.value.codeInvalid, "warning");
		return;
	}
	emit("submit", { email: email.value, code: verifyCode.value });
};

const handleNext = () => {
	touched.value = true;
	if (!isValid.value) return;
	emit("next", email.value);
};

watch(
	() => props.presetEmail,
	newVal => {
		if (newVal) email.value = newVal;
	},
	{ immediate: true }
);

defineExpose({
	getFilledFieldCount,
});
</script>

<style lang="scss" scoped>
.email-step {
	.step-title {
		margin: 0 0 12px;
		font-size: 24px;
		font-weight: 700;
		line-height: 1.35;
		color: #333333;
	}
	.step-desc {
		margin: 0 0 24px;
		font-size: 16px;
		line-height: 1.5;
		color: #7f7f7f;
	}
	.clear-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 18px;
		height: 18px;
		padding: 0;
		background: transparent;
		border: 0;
		cursor: pointer;
		img {
			width: 18px;
			height: 18px;
		}
	}
	.error-text {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-top: 10px;
		font-size: 13px;
		color: #f35b5b;
		img {
			width: 16px;
			height: 16px;
		}
	}
	.switch-link {
		margin-top: 14px;
		padding: 0;
		background: transparent;
		border: 0;
		font-size: 15px;
		color: #4a7dff;
		cursor: pointer;
	}

	&--integrated {
		.email-field {
			display: flex;
			align-items: center;
			width: 100%;
			height: 60px !important;
			padding: 0 12px 0 16px;
			background: #ffffff;
			border: 1px solid #e5e5e5;
			border-radius: 8px;
			box-sizing: border-box;
			&.invalid {
				border-color: #f35b5b;
			}
		}
		.email-input {
			flex: 1;
			height: 100%;
			border: 0;
			outline: none;
			font-size: 18px !important;
			color: #333333;
			background: transparent;
			&::placeholder {
				color: #b7b7b7;
			}
		}
		.code-row {
			display: flex;
			align-items: center;
			width: 100%;
			gap: 12px;
			margin-top: 16px;
			box-sizing: border-box;
		}
		.code-input {
			flex: 1;
			min-width: 0;
			height: 60px;
			padding: 0 16px;
			border: 1px solid #e5e5e5;
			border-radius: 8px;
			outline: none;
			font-size: 18px;
			color: #333333;
			box-sizing: border-box;
			&::placeholder {
				color: #b7b7b7;
			}
		}
		.send-code-btn {
			flex: 0 0 132px;
			width: 132px;
			height: 60px;
			padding: 0 8px;
			background: #ffffff;
			border: 1px solid #f2af19;
			border-radius: 8px;
			font-size: 20px;
			color: #f2af19;
			cursor: pointer;
			white-space: nowrap;
			box-sizing: border-box;
			&:disabled {
				cursor: not-allowed;
				opacity: 0.7;
			}
		}
		.kefu {
			display: block;
			font-size: 16px;
			color: #999999;
			margin: 20px 0 40px;
			text-decoration: none;
			cursor: pointer;
			&:hover {
				color: #777777;
			}
		}
		.next-btn-container {
			display: flex;
			justify-content: center;
			margin-top: 20px;
		}
		.next-btn {
			width: 60%;
			height: 60px;
			border: 0;
			border-radius: 8px;
			background: #e5e5e5;
			font-size: 16px;
			font-weight: 500;
			color: #ffffff;
			cursor: not-allowed;
			&.enabled {
				background: #000000;
				cursor: pointer;
			}
		}
	}

	&--step {
		.email-field {
			display: flex;
			align-items: center;
			height: 46px;
			padding: 0 12px 0 16px;
			background: #ffffff;
			border: 1px solid #3b3b3b;
			border-radius: 10px;
			transition: border-color 0.2s ease;
			&.invalid {
				border-color: #f35b5b;
			}
		}
		.email-input {
			flex: 1;
			height: 100%;
			border: 0;
			outline: none;
			font-size: 16px;
			color: #333333;
			background: transparent;
			&::placeholder {
				color: #b7b7b7;
			}
		}
		.error-text {
			line-height: 1.4;
		}
		.switch-link {
			line-height: 1.4;
		}
		.next-btn {
			width: 100%;
			height: 46px;
			margin-top: 22px;
			border: 0;
			border-radius: 10px;
			background: #d9d9d9;
			font-size: 18px;
			font-weight: 600;
			color: #ffffff;
			cursor: not-allowed;
			transition: background-color 0.2s ease;
			&.enabled {
				background: #111111;
				cursor: pointer;
			}
		}
	}
}
</style>
